import { NetworkStatus } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import { useWindowDimensions } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useDebouncedCallback } from "use-debounce";

import LeaguesView from "./LeaguesView";
import LeagueItem from "@/features/roster-management/components/molecules/LeagueItem";

import { ListRenderItem } from "@/components/atoms/FlashList";
import { Spinner } from "@/components/atoms/Spinner";
import { spacingForDevicesWithoutNotch } from "@/config/constants";
import { useCurrentUser } from "@/features/auth";
import {
  RosterManagementLeagueFragment,
  useRosterManagementListLeaguesQuery,
} from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import useSetPageTitle from "@/hooks/useSetPageTitle";

/**
 * Leagues Container
 *
 * Manages league data fetching, search, and pagination for the roster
 * management league selection screen. Organizations associate with leagues
 * to determine which players appear in their roster view.
 * @module features/roster-management/screens/Leagues/LeaguesContainer
 */
const LeaguesContainer = () => {
  const currentUser = useCurrentUser();
  const { bottom } = useSafeAreaInsets();
  const [searchText, setSearchText] = useState<string>("");
  const isSmallScreen = useMobileScreen();
  const { data, fetchMore, refetch, loading, networkStatus } =
    useRosterManagementListLeaguesQuery({
      variables: { search: searchText },
      notifyOnNetworkStatusChange: true,
    });

  const isRefreshing = useMemo(
    () => networkStatus === NetworkStatus.refetch,
    [networkStatus]
  );

  const leagues = useMemo(
    () => data?.listLeagues?.edges.map(edge => edge.node) ?? [],
    [data?.listLeagues?.edges]
  );

  const endCursor = useMemo(
    () =>
      data?.listLeagues?.pageInfo.hasNextPage
        ? data?.listLeagues?.pageInfo.endCursor
        : null,
    [
      data?.listLeagues?.pageInfo.endCursor,
      data?.listLeagues?.pageInfo.hasNextPage,
    ]
  );

  const handleLoadMore = useCallback(() => {
    if (endCursor && !loading) {
      fetchMore({
        variables: { after: endCursor },
      });
    }
  }, [endCursor, fetchMore, loading]);

  const handleRefresh = useCallback(() => refetch().catch(() => {}), [refetch]);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
  }, []);
  const handleSearchDebounce = useDebouncedCallback(handleSearch, 300);

  const renderItem: ListRenderItem<RosterManagementLeagueFragment> =
    useCallback(
      ({ item }) => (
        <LeagueItem
          item={item}
          organizationId={currentUser?.organizationId ?? ""}
        />
      ),
      [currentUser?.organizationId]
    );

  const renderListFooterComponent = useCallback(
    () =>
      loading && !isRefreshing && leagues.length ? (
        <Spinner size="large" color="white" />
      ) : null,
    [loading, isRefreshing, leagues.length]
  );

  const { width } = useWindowDimensions();
  // Replaces useBreakpointValue({ default: width, md: isSmallScreen ? 750 : 640 }):
  // cap at the md breakpoint (≥768px) and use full width below it.
  const widthContent = width >= 768 ? (isSmallScreen ? 750 : 640) : width;

  useSetPageTitle("League Selection");

  return (
    <LeaguesView
      leagues={leagues}
      loading={loading}
      widthContent={widthContent}
      refreshing={isRefreshing}
      renderItem={renderItem}
      renderListFooterComponent={renderListFooterComponent}
      onLoadMore={handleLoadMore}
      onRefresh={handleRefresh}
      onSearch={handleSearchDebounce}
      bottomInset={bottom + spacingForDevicesWithoutNotch}
    />
  );
};

export default LeaguesContainer;
